import React, { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogContent, IconButton, Tooltip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HeadphonesOutlinedIcon from '@mui/icons-material/HeadphonesOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoredUserData } from '../../authentication/userSession';
import plannerCompanion from '../../assets/images/ielts-planner-companion-purple.png';
import './PteDashboardOffer.css';

export default function PteDashboardOffer({ enabled = true }) {
  const user = useStoredUserData();
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showBar, setShowBar] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const visitRef = useRef(null);
  const eligible = enabled && location.pathname === '/pte-dashboard' &&
    Number(user?.PortalID) === 1 && Boolean(user?.UserId) && !user?.IsSubscribed;

  useEffect(() => {
    if (!eligible) {
      setShowModal(false);
      setShowBar(false);
      visitRef.current = null;
      return;
    }
    const dailyKey = `pte-dashboard-offer-last-shown-${user.PortalID}-${user.UserId}`;
    const today = new Date().toDateString();
    const visitKey = `${dailyKey}:${today}`;
    if (visitRef.current !== visitKey) {
      let showToday = true;
      try {
        showToday = localStorage.getItem(dailyKey) !== today;
        if (showToday) localStorage.setItem(dailyKey, today);
      } catch (_error) {
        // Keep the dashboard usable when browser storage is unavailable.
      }
      setShowModal(showToday);
      visitRef.current = visitKey;
    }
    setShowBar(true);
    setCopied(false);
    setCopyError(false);
  }, [eligible, user?.PortalID, user?.UserId, location.key]);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyCode = async () => {
    setCopyError(false);
    try {
      await navigator.clipboard.writeText('pteswift20');
      setCopied(true);
    } catch (_error) {
      setCopied(false);
      setCopyError(true);
    }
  };

  const upgrade = () => {
    setShowModal(false);
    setShowBar(false);
    navigate('/plan-info', { state: { autoPromoCode: 'pteswift20' } });
  };

  if (!eligible) return null;

  const copyStatus = copyError ? 'Could not copy. Use code pteswift20.' : copied ? 'Code copied!' : '';

  return (
    <>
      <Dialog open={showModal} onClose={() => setShowModal(false)} maxWidth="sm" fullWidth
        aria-labelledby="pte-upgrade-heading" PaperProps={{ className: 'pte-upgrade-offer' }}>
        <DialogContent>
          <IconButton className="pte-upgrade-close" aria-label="Close offer" onClick={() => setShowModal(false)}><CloseIcon fontSize="small" /></IconButton>
          <h2 id="pte-upgrade-heading">Make every PTE practice count.</h2>
          <p className="pte-upgrade-subtitle">Your target score deserves more than guesswork.</p>
          <div className="pte-upgrade-focus">
            <div><h3>More practice.<br /><span>Better prepared.</span></h3><p>Prepare for your target score with mock tests and AI feedback.</p></div>
            <img src={plannerCompanion} alt="" width="150" height="166" />
          </div>
          <div className="pte-upgrade-benefits">
            <div><AltRouteOutlinedIcon /><div><strong>More PTE mock tests</strong><span>Full, sectional &amp; question-type tests.</span></div></div>
            <div><ForumOutlinedIcon /><div><strong>AI speaking &amp; writing feedback</strong><span>See where your answers need improvement.</span></div></div>
            <div><HeadphonesOutlinedIcon /><div><strong>All four skills, one place</strong><span>Speaking, Writing, Reading &amp; Listening.</span></div></div>
          </div>
          <div className="pte-upgrade-savings">
            <strong>20% OFF</strong>
            <Tooltip title={copied ? 'Code copied!' : 'Copy promo code'}>
              <Button className="pte-upgrade-copy" aria-label={copied ? 'Promo code copied' : 'Copy promo code pteswift20'}
                onClick={copyCode} endIcon={copied ? <CheckOutlinedIcon /> : <ContentCopyOutlinedIcon />}>
                pteswift20
              </Button>
            </Tooltip>
          </div>
          <p role="status" className={copyError ? 'pte-upgrade-copy-error' : 'pte-upgrade-copy-status'}>{copyStatus}</p>
          <Button className="pte-upgrade-primary" onClick={upgrade} variant="contained" endIcon={<ArrowForwardIcon />}>Upgrade &amp; save 20%</Button>
          <Button className="pte-upgrade-later" onClick={() => setShowModal(false)}>Continue free</Button>
        </DialogContent>
      </Dialog>
      {showBar && !showModal && (
        <section className="pte-dashboard-offer-bar" aria-label="PTE upgrade offer">
          <div className="pte-offer-bar-message">
            <img src={plannerCompanion} alt="" width="48" height="60" />
            <div><h3>More practice. <span>Better prepared.</span></h3><p>Clear AI feedback. More PTE mock tests.</p></div>
          </div>
          <div className="pte-offer-bar-actions">
            <Tooltip title={copied ? 'Code copied!' : 'Copy promo code'}>
              <Button className="pte-offer-bar-copy" aria-label={copied ? 'Promo code copied' : 'Copy promo code pteswift20'}
                onClick={copyCode} endIcon={copied ? <CheckOutlinedIcon /> : <ContentCopyOutlinedIcon />}>pteswift20</Button>
            </Tooltip>
            <Button className="pte-offer-bar-upgrade" onClick={upgrade} endIcon={<ArrowForwardIcon />}>Upgrade and save 20%</Button>
          </div>
          <IconButton className="pte-offer-bar-close" aria-label="Dismiss offer" onClick={() => setShowBar(false)}><CloseIcon fontSize="small" /></IconButton>
          <p role="status" className={copyError ? 'pte-offer-bar-error' : 'pte-upgrade-copy-status'}>{copyStatus}</p>
        </section>
      )}
    </>
  );
}
